import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import type { AgGridReact } from "ag-grid-react"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { useFlags } from "~/features/feature-flags"
import { getGridApiById, isApplyingView, useIsGridRegistered } from "~/hooks/useGridState"
import { NOOS_PRODUCT_TABLE_ID } from "~/constants"
import { forecastingDefaultColumnIdsToDisplay } from "~/constants/forecasting"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useFilters } from "~/hooks/useFilters"
import { useGetCollections } from "~/hooks/useCollections"
import { filterVariants } from "~/utils/filter"
import { useTableSearch } from "~/hooks/useTableSearch"
import { max, min } from "lodash"
import { WorkspacePage } from "~/components/design-system/pages"
import CollectionInfoCard from "~/components/demand-plan/CollectionInfoCard"
import { useCollectionContext } from "~/context/CollectionProvider"
import { SalesAndForecastGraphCard } from "~/components/home/SalesAndForecastGraphCard"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { collectionFilterColumns, ProductTableColumn } from "~/utils/productTableUtils"
import {
  commitDisplayedColumns,
  liveColumns,
  shownManagedColumnIds,
} from "~/components/planning/ProductTable/utils/columnVisibility"
import { ProductTableType } from "~/constants/productTable"
import { ChartLine, Faders } from "@phosphor-icons/react"
import ExportForecastPopover from "~/components/forecast/ExportForecastPopover"
import { useVariantContext } from "~/context/VariantProvider"
import FullPageDialog from "~/components/reusable/dialogs/FullPageDialog"
import ForecastEvaluationTable from "~/components/forecastEvaluation/ForecastEvaluationTable"
import { AdjustmentManagerDialog } from "~/components/forecast/dialogs/AdjustmentManagerDialog"

export const Route = createFileRoute("/_auth/demand-planning/noos/$collectionId/_layout/")({
  component: RouteComponent,
})

function RouteComponent() {
  const gridRef = useRef<AgGridReact<IVariantSummary>>(null)

  const { effectiveSearchText } = useTableSearch(NOOS_PRODUCT_TABLE_ID)
  const [isEvaluationDialogOpen, setIsEvaluationDialogOpen] = useState(false)
  const [isAdjustmentsOpen, setIsAdjustmentsOpen] = useState(false)

  const { forecastEvaluation: forecastEvaluationFlag } = useFlags()

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const { variants: rawVariants } = useVariantContext()
  const { collections, collectionGroups } = useCollectionContext()

  const collectionGroup = useMemo(() => {
    const groupId = collections[0]?.collectionGroupId
    return groupId != null ? collectionGroups?.find((g) => g.id === groupId) : undefined
  }, [collections, collectionGroups])

  const { filters, filteredCollections } = useFilters()

  const variants = useMemo(() => {
    return filterVariants(filters, rawVariants, effectiveSearchText)
  }, [rawVariants, filters, effectiveSearchText])

  const variantIds = useMemo(() => {
    return variants.map((v) => v.variantId)
  }, [variants])

  const collectionIds = useMemo(() => {
    return filteredCollections?.map((c) => c.id) ?? []
  }, [filteredCollections])

  const collectionStartDate = useMemo(() => {
    return min(filteredCollections.map((c) => c.startDate))
  }, [filteredCollections])

  const collectionEndDate = useMemo(() => {
    return max(filteredCollections.map((c) => c.endDate))
  }, [filteredCollections])

  // A view being applied owns its columns, and `filteredCollections` is empty until collections
  // load, so both cases wait.
  const { isSuccess: collectionsLoaded } = useGetCollections()
  const hasCollections = filteredCollections.length > 0
  const isGridRegistered = useIsGridRegistered(NOOS_PRODUCT_TABLE_ID)
  useEffect(() => {
    const api = getGridApiById(NOOS_PRODUCT_TABLE_ID)
    if (!collectionsLoaded || !isGridRegistered || !api || isApplyingView(NOOS_PRODUCT_TABLE_ID))
      return
    commitDisplayedColumns(
      api,
      collectionFilterColumns(shownManagedColumnIds(liveColumns(api)), hasCollections),
    )
  }, [hasCollections, collectionsLoaded, isGridRegistered])

  const inspector = (
    <>
      <CollectionInfoCard
        collections={collections}
        collectionGroup={collectionGroup}
        variants={rawVariants}
      />
      <SalesAndForecastGraphCard
        variantIds={variantIds}
        warehouseId={selectedWarehouseId}
        startDate={collectionStartDate ?? undefined}
        endDate={collectionEndDate ?? undefined}
        channel={selectedChannel}
        isNoos
        expandable
      />
    </>
  )

  return (
    <>
      <WorkspacePage
        fillContent
        viewConfiguration={
          <ViewConfiguration
            tableId={NOOS_PRODUCT_TABLE_ID}
            showChannelSelect
            exportButton={
              <ExportForecastPopover
                variants={variants}
                filters={filters}
                gridRef={gridRef}
                showSizeSplit
                showMonthlyOption
                showNetOptions
                compact
              />
            }
            moreActions={[
              {
                id: "adjustments",
                label: "Adjustments",
                icon: <Faders />,
                onClick: () => setIsAdjustmentsOpen(true),
              },
              forecastEvaluationFlag && {
                id: "forecast-evaluation",
                label: "Forecast Evaluation",
                icon: <ChartLine />,
                onClick: () => setIsEvaluationDialogOpen(true),
              },
            ]}
          />
        }
        inspector={inspector}
        inspectorWidth="lg"
      >
        <ProductTableV2
          id={NOOS_PRODUCT_TABLE_ID}
          gridRef={gridRef}
          variants={variants}
          warehouseId={selectedWarehouseId}
          channel={selectedChannel}
          defaultColumnIdsToDisplay={forecastingDefaultColumnIdsToDisplay}
          masterDetail
          enableSearch={true}
          collectionIds={collectionIds}
          listCollectionColumnsInChooser
          type={ProductTableType.NoosDemandPlan}
          isNoos
          leftPinnedColumns={[
            ProductTableColumn.ForecastQuality,
            ProductTableColumn.Forecast,
            ProductTableColumn.Sales,
          ]}
        />
      </WorkspacePage>

      <AdjustmentManagerDialog
        variantIds={variantIds}
        allVariants={variants}
        open={isAdjustmentsOpen}
        onOpenChange={setIsAdjustmentsOpen}
      />

      {isEvaluationDialogOpen && (
        <FullPageDialog
          title="Forecast Evaluation"
          onClose={() => setIsEvaluationDialogOpen(false)}
        >
          <div className="p-4 h-full">
            <ForecastEvaluationTable />
          </div>
        </FullPageDialog>
      )}
    </>
  )
}
